import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const isEditable = (element) => element?.matches?.('input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="file"]):not([type="button"]):not([type="submit"]), textarea, [contenteditable="true"], [role="textbox"]');

export function readViewport(layoutHeight = window.innerHeight, previous) {
  const viewport = window.visualViewport;
  // Pinch zoom should magnify the page, not rebuild its layout at a smaller size.
  const zoomed = viewport && Math.abs(viewport.scale - 1) > 0.01;
  if (zoomed && previous) return previous;
  const height = Math.max(0, !zoomed && viewport ? viewport.height : window.innerHeight);
  const offsetTop = !zoomed && viewport ? Math.max(0, viewport.offsetTop) : 0;
  return {
    height,
    layoutHeight: Math.max(layoutHeight, window.innerHeight, height),
    offsetTop,
    bottom: Math.max(0, window.innerHeight - height - offsetTop),
    // Focus may move through body or a toolbar before the keyboard closes.
    // Keep the keyboard layout until the measured viewport actually recovers.
    keyboardOpen: !zoomed && Boolean(isEditable(document.activeElement) || previous?.keyboardOpen) && Math.max(window.innerHeight, layoutHeight) - height > 120,
  };
}

export default function useViewport() {
  const [viewport, setViewport] = useState(readViewport);
  const lastViewport = useRef(viewport);
  const layout = useRef({ height: window.innerHeight, width: window.innerWidth });
  useLayoutEffect(() => {
    const update = () => {
      const zoomed = window.visualViewport && Math.abs(window.visualViewport.scale - 1) > 0.01;
      if (!zoomed) {
        if (Math.abs(window.innerWidth - layout.current.width) > 80) {
          // After rotation, the old width approximates the unobstructed height.
          layout.current.height = lastViewport.current.keyboardOpen
            ? Math.max(window.innerHeight, layout.current.width) : window.innerHeight;
        } else if (!isEditable(document.activeElement) && !lastViewport.current.keyboardOpen) {
          layout.current.height = window.innerHeight;
        } else {
          layout.current.height = Math.max(layout.current.height, window.innerHeight);
        }
        layout.current.width = window.innerWidth;
      }
      const next = readViewport(layout.current.height, lastViewport.current);
      lastViewport.current = next;
      setViewport((previous) => Object.keys(next).every((key) => next[key] === previous[key]) ? previous : next);
    };
    const visualViewport = window.visualViewport;
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    visualViewport?.addEventListener('resize', update);
    visualViewport?.addEventListener('scroll', update);
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      visualViewport?.removeEventListener('resize', update);
      visualViewport?.removeEventListener('scroll', update);
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', update);
    };
  }, []);
  return viewport;
}

export function useViewportCss() {
  const viewport = useViewport();
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--app-viewport-height', `${viewport.height}px`);
    root.style.setProperty('--app-layout-height', `${viewport.layoutHeight}px`);
    root.style.setProperty('--app-viewport-top', `${viewport.offsetTop}px`);
    root.style.setProperty('--app-viewport-bottom', `${viewport.bottom}px`);
    root.dataset.keyboardOpen = String(viewport.keyboardOpen);
  }, [viewport]);
  useEffect(() => () => {
    const root = document.documentElement;
    ['--app-viewport-height', '--app-layout-height', '--app-viewport-top', '--app-viewport-bottom'].forEach((name) => root.style.removeProperty(name));
    delete root.dataset.keyboardOpen;
  }, []);
}
